import { toast } from '@apps/web/lib/app_notifications';
import { type ShiftUpdateResponse } from '@libraries/typescript/api_client';

type EhrWritebackStatus = NonNullable<
    | ShiftUpdateResponse['ehr_assignment_status']
    | ShiftUpdateResponse['ehr_unassign_status']
    | ShiftUpdateResponse['ehr_time_update_status']
>;

const notificationOptions = {
    persistNotification: true,
    notificationType: 'shift_action',
} as const;

function formatStatus(status: EhrWritebackStatus): string {
    return status.replace(/_/g, ' ');
}

function showWritebackToast(options: {
    readonly genericSuccessTitle: string;
    readonly status: EhrWritebackStatus | null | undefined;
    readonly successTitle: string;
    readonly warningTitle: string;
}): void {
    if (
        options.status === null ||
        options.status === undefined ||
        options.status === 'disabled' ||
        options.status === 'not_available'
    ) {
        // `disabled` (writeback feature off — the default during rollout) and
        // `not_available` (no connected EHR / sandbox) both mean nothing was
        // attempted, so there is nothing to reconcile. Report an ordinary
        // success instead of a persistent “fix this by hand” warning. Genuine
        // failed / superseded / unsupported outcomes still warn below.
        toast.success(options.genericSuccessTitle, notificationOptions);
        return;
    }

    if (options.status === 'succeeded') {
        toast.success(options.successTitle, notificationOptions);
        return;
    }

    const toastOptions = {
        description: `EHR writeback status: ${formatStatus(options.status)}. Reconcile this shift manually in the EHR.`,
        ...notificationOptions,
    };

    if (options.status === 'failed') {
        toast.error(options.warningTitle, toastOptions);
        return;
    }

    toast.warning(options.warningTitle, toastOptions);
}

export function showShiftTimeUpdateToast(response: ShiftUpdateResponse): void {
    if (response.ehr_time_update_status === 'rejected') {
        // The EHR refused the new times and the shift kept its previous
        // times, so this is a rejection of the edit, not a reconcile task.
        toast.error('Shift time change rejected by the EHR', {
            description: `${response.ehr_time_update_error ?? 'The EHR did not accept the new times.'} The previous times were kept.`,
            ...notificationOptions,
        });
        return;
    }
    if (response.ehr_time_update_status === 'missing') {
        toast.error('Shift no longer exists in the EHR', {
            description: 'The previous times were kept.',
            ...notificationOptions,
        });
        return;
    }
    showWritebackToast({
        genericSuccessTitle: 'Shift updated successfully',
        status: response.ehr_time_update_status,
        successTitle: 'Shift updated in Phoebe and the EHR',
        warningTitle: 'Shift updated in Phoebe, but not the EHR',
    });
}

export function showCaregiverWritebackToast(
    caregiverId: string,
    response: ShiftUpdateResponse,
    ehrName?: string,
): void {
    const status =
        caregiverId === 'unassigned'
            ? response.ehr_unassign_status
            : response.ehr_assignment_status;

    if (status === 'missing') {
        const title =
            caregiverId === 'unassigned'
                ? 'Caregiver removed in Phoebe only'
                : 'Caregiver assigned in Phoebe only';
        toast.warning(title, {
            description: `This shift no longer exists in ${ehrName ?? 'the EHR'}.`,
            ...notificationOptions,
        });
        return;
    }
    showWritebackToast({
        genericSuccessTitle: 'Caregiver assignment updated successfully',
        status,
        successTitle: `Caregiver assignment updated in Phoebe and ${ehrName ?? 'the EHR'}`,
        warningTitle: 'Caregiver assignment updated in Phoebe, but not the EHR',
    });
}
